/* eslint-disable no-useless-escape */
import dayjs from "dayjs";
import { format, parse, isValid } from "date-fns";
import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import axios from "../config/httpConfig";
import { downloadPage, detailedViewDownload } from "../redux/slices/DocumentSlice";
import { message } from "antd";
import { FIN_TEMPLATE_CONFIG } from "../constants/constants";
import * as XLSX from "xlsx";

export const blockNumberInputWheel = (event) => {
  event.currentTarget.blur();
};

export const DateFormatter = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/** Combine a date-picker value with the current local time as ISO (for txn timestamps). */
export const toTransactionDateIso = (value) => {
  if (!value) return undefined;
  const selected = dayjs(value);
  const now = dayjs();
  return selected
    .hour(now.hour())
    .minute(now.minute())
    .second(now.second())
    .millisecond(0)
    .toISOString();
};

export const convertToPercentage = (str) => {
  if (str === null || str === undefined || str === "" || Number(str) === 0) {
    return "0%";
  }
  const pct = Number(str) * 100;
  if (!Number.isFinite(pct)) return "0%";
  // 16.00 → 16%, 16.05 → 16.05%, 4.56 → 4.56%
  return `${parseFloat(pct.toFixed(2))}%`;
};

/** DB fraction (0.075) → percent input string ("7.5"), no trailing zeros */
export const formatDiscountRatePercent = (rate) => {
  if (rate === null || rate === undefined || rate === "") return "";
  const pct = Number(rate) * 100;
  if (!Number.isFinite(pct)) return "";
  return String(parseFloat(pct.toFixed(2)));
};

/** Percent input (7.5) → DB fraction (0.075) */
export const percentToDiscountRate = (percent) => {
  const num = Number(percent);
  if (!Number.isFinite(num)) return null;
  return Number((num / 100).toFixed(6));
};

export const formatMaxFundRequiredPercent = (rate) => {
  if (rate === null || rate === undefined || rate === "") return "";
  const pct = Number(rate) * 100;
  if (!Number.isFinite(pct)) return "";
  return String(parseFloat(pct.toFixed(2)));
};

export const percentToMaxFundRequiredPct = (percent) => {
  const num = Number(percent);
  if (!Number.isFinite(num)) return null;
  return Number((num / 100).toFixed(2));
};

export const resolveMaxFundRequiredPct = (value) => {
  const pct = Number(value);
  if (!Number.isFinite(pct) || pct < 0.6 || pct > 0.8) return 0.8;
  return pct;
};

export const maxFundAllowedAmount = (totalInv, maxFundRequiredPct = 0.8) => {
  const pctBasisPoints = Math.round(resolveMaxFundRequiredPct(maxFundRequiredPct) * 10000);
  return Math.floor((Number(totalInv) * pctBasisPoints) / 10000);
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

export const dealsClosesIn = (date) => {
  const now = dayjs();
  const expiryDate = dayjs(date).endOf("day");

  const diffInDays = expiryDate.diff(now, "day");

  if (diffInDays > 0) {
    return `${diffInDays} days`;
  }

  const diffInHours = expiryDate.diff(now, "hour");
  if (diffInHours > 0) {
    return `${diffInHours} hrs`;
  }

  const diffInMinutes = expiryDate.diff(now, "minute");
  return diffInMinutes > 0 ? `${diffInMinutes} mins` : "Expired";
};

export const disputeAgeCalculation = (value) => {
  const today = dayjs();
  const selectedDate = dayjs(value);
  const disputeAge = selectedDate.isValid()
    ? today.diff(selectedDate, "day")
    : "";

  return disputeAge;
};

export const formatCurrency = (amount) => {
  const rounded = Math.round(Number(amount) || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
};

export const formatCurrencyTwoDec = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatAmountTwoDec = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "0.00";
  return value.toFixed(2);
};

export const roundAmountTwoDec = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

export const formatToLacCr = (amount) => {
  if (amount >= 10000000) {
    return (amount / 10000000).toFixed(2) + " Cr"; // For Crores
  } else if (amount >= 100000) {
    return (amount / 100000).toFixed(2) + " Lac"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(amount); // For smaller amounts
};

export const formatToLCr = (amount) => {
  if (amount >= 10000000) {
    return (amount / 10000000).toFixed(0) + " Cr"; // For Crores
  } else if (amount >= 100000) {
    return (amount / 100000).toFixed(0) + " L"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(amount); // For smaller amounts
};

export const formatTimeAgo = (timestamp) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return `${mins} min${mins > 1 ? "s" : ""} ago`;
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  }
  const days = Math.floor(diffSec / 86400);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export const truncateDescription = (description, limit) => {
  if (description?.length <= limit) {
    return description;
  }
  return `${description?.slice(0, limit)}...`;
};

export const calculateSoldOutPercentage = (fundedAmt, approvedFundAmt) => {
  return ((Number(fundedAmt) / Number(approvedFundAmt)) * 100).toFixed(0);
};

export const calculateReturns = (annualDiscountRate) => {
  return (Number(annualDiscountRate) * 100).toFixed(0);
};

export const calculateDiscountAmount = (
  dealAmount,
  annualDiscountRate,
  tenure
) => {
  return (
    (Number(dealAmount) * Number(annualDiscountRate) * Number(tenure)) /
    365
  ).toFixed(2);
};

export const calculateAvailableAmountToInvest = (
  approvedFundAmt,
  fundedAmt
) => {
  return (Number(approvedFundAmt) - Number(fundedAmt)).toFixed(2);
};

export const calculateAmountToInvestNow = (dealAmount, discountAmount) => {
  return (Number(dealAmount) - Number(discountAmount)).toFixed(2);
};

export const calculateNetReturns = (
  discountAmount,
  platformFee,
  penalCharge
) => {
  return (
    Number(discountAmount) -
    (Number(platformFee) + Number(penalCharge))
  ).toFixed(2);
};

export const calculatePlatformFee = (dealAmount, platformFee, tenure) => {
  return (
    ((Number(dealAmount) * Number(platformFee)) / 365) *
    Number(tenure)
  ).toFixed(2);
};

export const calculateExpectedNetProfit = (discountAmount, platformFee) => {
  return (Number(discountAmount) - Number(platformFee)).toFixed(2);
};

export const calculateExpectedNetRepayment = (
  amountToInvestNow,
  discountAmount,
  platformFee
) => {
  return (
    Number(amountToInvestNow) +
    (Number(discountAmount) - Number(platformFee))
  ).toFixed(2);
};

export const calculateDateRange = (filterId) => {
  const now = dayjs();
  let start, end;

  switch (filterId) {
    case "ORV0001": // Till Date
      start = "";
      end = "";
      break;
    case "ORV0002": // Last Month
      start = now.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0003": // Last 3 months
      start = now.subtract(3, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0004": // Last 6 months
      start = now.subtract(6, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0005": // Last 12 months
      start = now.subtract(12, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    default:
      start = "";
      end = "";
      break;
  }

  return { start, end };
};

export const formatCategoryName = (category) => {
  if (category.toLowerCase() === "roi") {
    return category.toUpperCase();
  }

  if (category === "industryTypes") {
    return "Industries";
  }

  return category
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatFee = (value, type = "amount") => {
  if (value === undefined || value === null) return "-";

  if (type === "percent") {
    return `${Math.round(value * 100)}%`; // nearest integer for percentages
  }

  if (type === "amount") {
    return `₹ ${Math.round(value)}`; // nearest rupee for amounts
  }

  return value;
};

export const formatOptionLabel = (category, label) => {
  // Append "%" for ROI and "days" for Maturity
  if (category === "roi") {
    return `${label}%`;
  }
  if (category === "maturity") {
    return `${label} days`;
  }
  return label;
};

export const maskEmail = (email) => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  return localPart[0] + "****" + localPart.slice(-1) + "@" + domain;
};

export const maskPhone = (phone) => {
  if (!phone) return "";
  return phone.slice(0, 4) + "****" + phone.slice(-2);
};

export const calculateRelativeTime = (givenDate) => {
  const today = dayjs();
  const date = dayjs(givenDate);

  if (!date.isValid()) {
    throw new Error("Invalid date provided");
  }

  const days = today.diff(date, "day");
  const months = today.diff(date, "month");
  const years = today.diff(date, "year");

  if (days < 30) {
    return `${days} day${days === 1 ? "" : "s"}`;
  } else if (months < 12) {
    return `${months} month${months === 1 ? "" : "s"}`;
  } else {
    return `${years} year${years === 1 ? "" : "s"}`;
  }
};

export const capitalizeWords = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char?.toUpperCase());
};

export const capitalizeEntireWord = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w+\b/g, (word) => word.toUpperCase());
};

export const generateStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

export const formatParticulars = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Collect period/year keys from pivoted financial metric rows (excludes particulars). */
export const getFinancialMetricPeriodKeys = (rows = []) => {
  const periodKeys = [];
  const seen = new Set();

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== "object") continue;
    for (const key of Object.keys(row)) {
      if (key === "particulars") continue;
      if (!seen.has(key)) {
        seen.add(key);
        periodKeys.push(key);
      }
    }
  }

  return periodKeys.sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
};

/** Build Ant Design table columns from uploaded financial metric rows. */
export const buildFinancialMetricColumns = (rows = [], particularsRender) => {
  const periodKeys = getFinancialMetricPeriodKeys(rows);

  return [
    {
      title: "Particulars",
      dataIndex: "particulars",
      key: "particulars",
      width: 200,
      fixed: "left",
      ...(particularsRender ? { render: particularsRender } : {}),
    },
    ...periodKeys.map((period) => ({
      title: String(period),
      dataIndex: period,
      key: String(period),
      width: 150,
      align: "center",
    })),
  ];
};

export const toTitleCase = (sentence) => {
  if (!sentence || typeof sentence !== "string") return "";

  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatDateTime = (dateString) => {
  const date = dateString ? new Date(dateString) : new Date();

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day}/${month}/${year}_${hours}:${minutes}${ampm}`;
};

export const parseUnknownDate = (inputDate) => {
  const possibleFormats = [
    "yyyy-MM-dd", // ISO 8601
    "dd-MM-yyyy", // European format
    "MM/dd/yyyy", // US format
    "yyyy/MM/dd", // Slash-separated ISO
    "dd/MM/yyyy", // Slash-separated European
    "MMMM d, yyyy", // Full month, day, year
    "d MMMM yyyy", // Day full month year
    "yyyy-MM-dd'T'HH:mm:ssX", // ISO 8601 with time
  ];

  let reqFormat = inputDate;

  // Handle two-digit years (MM/DD/YY)
  const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})(?!\d)/;
  if (regex.test(reqFormat)) {
    reqFormat = reqFormat.replace(regex, (match, mm, dd, yy) => {
      const fullYear = parseInt(yy, 10) > 50 ? `19${yy}` : `20${yy}`;
      return `${mm}/${dd}/${fullYear}`;
    });
  }

  for (const formatString of possibleFormats) {
    const parsedDate = parse(reqFormat, formatString, new Date());
    if (isValid(parsedDate)) {
      return format(parsedDate, "yyyy-MM-dd");
    }
  }

  // Return null if no format matches
  return null;
};

export async function handleDeleteFile(fileUrl) {
  const fileKey = fileUrl.split(".com/")[1];

  const res = await axios.delete("/api/fileDelete", {
    data: { fileKey }
  });

  return res.data;
}

export async function handleUploadFile(file, path, onProgress) {
  let fileName;
  const res = await axios.post("/api/fileUpload", {
    fileName: file.name,
    fileType: file.type,
    folderPath: path,
  });
  const { uploadUrl, fileUrl } = res;

  fileName = file.name || `merged_${Date.now()}.pdf`;

  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });

  return { fileUrl, fileName };
}

export const ZipPdfMerger = async (file, path, onProgress) => {
  const zip = await JSZip.loadAsync(file);
  const pdfBuffers = [];

  const entries = Object.entries(zip.files)
    .filter(([name, file]) => !file.dir && name.toLowerCase().endsWith(".pdf"))
    .sort(([a], [b]) => a.localeCompare(b));

  // eslint-disable-next-line no-unused-vars
  for (const [filename, fileObj] of entries) {
    const pdfData = await fileObj.async("uint8array");
    pdfBuffers.push(pdfData);
  }

  if (pdfBuffers.length === 0) {
    return;
  }

  const mergedPdf = await PDFDocument.create();

  for (const pdfBytes of pdfBuffers) {
    const pdf = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedPdfBytes = await mergedPdf.save();
  const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
  const pdfFile = new File([blob], `merged_${Date.now()}.pdf`, {
    type: "application/pdf",
  });

  const res = await handleUploadFile(pdfFile, path, onProgress);

  return res;
};

export const confirmPasswordRules = [
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value)
        return Promise.reject(new Error("Please confirm your Password!"));
      if (getFieldValue("enterPassword") !== value)
        return Promise.reject(new Error("Passwords do not match!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  }),
];

export const passwordRules = [
  {
    required: true,
    message: "Password cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8)
        return Promise.reject(
          new Error("Password must be at least 8 characters!")
        );
      if (!/[A-Z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one uppercase letter!")
        );
      if (!/[a-z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one lowercase letter!")
        );
      if (!/\d/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one number!")
        );
      if (!/[^A-Za-z0-9]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one special character!")
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Password cannot exceed 256 characters!")
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const phoneNumberRules = (type) => [
  {
    required: true,
    whitespace: true,
    message: `${type} Phone Number cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error(`${type} Phone Number cannot be empty!`))
          : Promise.resolve();
      }
      if (!/^[6-9]\d{9}$/.test(trimmed))
        return Promise.reject(
          new Error(`Please enter a valid ${type} Phone Number`)
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const ifscRules = [
  {
    required: true,
    message: "IFSC code cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[A-Z]{4}[0][A-Z0-9]{6}$/.test(value))
        return Promise.reject(new Error("Please enter a valid IFSC code"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const accNumberRules = [
  {
    required: true,
    message: "Account number cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8 || value.length > 16)
        return Promise.reject(new Error("Please enter a valid account number"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const nameRules = (type) => [
  {
    required: true,
    whitespace: true,
    message: `${type} Name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error(`${type} Name cannot be empty!`))
          : Promise.resolve();
      }
      if (trimmed.length < 3)
        return Promise.reject(
          new Error(`${type} Name must be at least 3 characters!`)
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error(`${type} Name cannot exceed 256 characters!`)
        );
      if (!/^[a-zA-Z\s]+$/.test(trimmed))
        return Promise.reject(
          new Error(`${type} Name can only contain alphabets and spaces.`)
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const emailRules = (type) => [
  {
    required: true,
    whitespace: true,
    message: `${type} Email cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      const emailLabel = type ? `${type} Email` : "Email";
      if (!trimmed) {
        return value
          ? Promise.reject(new Error(`${emailLabel} cannot be empty!`))
          : Promise.resolve();
      }
      if (trimmed.length > 256)
        return Promise.reject(
          new Error(`${emailLabel} cannot exceed 256 characters!`)
        );
      if (!/^[a-zA-Z0-9@.]+$/.test(trimmed))
        return Promise.reject(
          new Error(`${emailLabel} can only contain letters, numbers, @ and .`)
        );
      if (
        !/^[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)+$/.test(
          trimmed
        )
      )
        return Promise.reject(new Error(`Please enter a valid ${emailLabel}.`));
      const localPart = trimmed.split("@")[0];
      if (!/[A-Za-z]/.test(localPart)) {
        return Promise.reject(
          new Error(`${emailLabel} cannot contain only numbers`)
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const companyNameRules = (type) => [
  {
    required: true,
    message: `${type} name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 3)
        return Promise.reject(
          new Error(`${type} name must be at least 3 characters!`)
        );
      if (value.length > 256)
        return Promise.reject(
          new Error(`${type} name cannot exceed 256 characters!`)
        );
      if (/^\d+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only numbers!`)
        );
      if (/^[^a-zA-Z0-9]+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only special characters!`)
        );
      if (!/[a-zA-Z]/.test(value))
        return Promise.reject(
          new Error(`${type} name must include at least one letter!`)
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const designationRules = [
  {
    required: true,
    whitespace: true,
    message: "Designation cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error("Designation cannot be empty!"))
          : Promise.resolve();
      }
      if (trimmed.length < 2)
        return Promise.reject(
          new Error("Designation must be at least 2 characters!")
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error("Designation cannot exceed 256 characters!")
        );
      if (!/^[a-zA-Z\s]+$/.test(trimmed))
        return Promise.reject(
          new Error("Designation can only contain alphabets and spaces.")
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address1Rules = [
  {
    required: true,
    whitespace: true,
    message: "Address Line 1 cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error("Address Line 1 cannot be empty!"))
          : Promise.resolve();
      }
      if (trimmed.length < 3)
        return Promise.reject(
          new Error("Address Line 1 must be at least 3 characters!")
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error("Address Line 1 cannot exceed 256 characters!")
        );
      if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
        return Promise.reject(
          new Error("Address Line 1 cannot contain only special characters!")
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address2Rules = [
  {
    required: true,
    whitespace: true,
    message: "Address Line 2 cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) return Promise.resolve();
      if (trimmed.length < 3)
        return Promise.reject(
          new Error("Address Line 2 must be at least 3 characters!")
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error("Address Line 2 cannot exceed 256 characters!")
        );
      if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
        return Promise.reject(
          new Error("Address Line 2 cannot contain only special characters!")
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const cityRules = [
  {
    required: true,
    whitespace: true,
    message: "City cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error("City cannot be empty!"))
          : Promise.resolve();
      }
      if (!/^[A-Za-z\s]+$/.test(trimmed))
        return Promise.reject(new Error("City should contain only Alphabets."));
      if (trimmed.length < 3)
        return Promise.reject(new Error("City must be at least 3 characters!"));
      if (trimmed.length > 256)
        return Promise.reject(new Error("City cannot exceed 256 characters!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const stateRules = [
  {
    required: true,
    whitespace: true,
    message: "State cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error("State cannot be empty!"))
          : Promise.resolve();
      }
      if (!/^[A-Za-z\s]+$/.test(trimmed))
        return Promise.reject(new Error("State should contain only Alphabets."));
      if (trimmed.length < 3)
        return Promise.reject(new Error("State must be at least 3 characters!"));
      if (trimmed.length > 256)
        return Promise.reject(new Error("State cannot exceed 256 characters!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const pincodeRules = [
  {
    required: true,
    whitespace: true,
    message: `Pincode cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error("Pincode cannot be empty!"))
          : Promise.resolve();
      }
      if (!/^\d+$/.test(trimmed))
        return Promise.reject(
          new Error("Pincode should contain only numbers.")
        );
      if (trimmed.length !== 6)
        return Promise.reject(new Error("Pincode should be exactly 6 digits."));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const entNameRules = [
  {
    required: true,
    message: `Enterprise Name cannot be empty!`,
  },
];

export const busNameRules = [
  {
    required: true,
    message: `Business Name cannot be empty!`,
  },
];

export const tenureRules = [
  {
    required: true,
    message: `Tenure cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^\d+$/.test(value))
        return Promise.reject(new Error("Tenure should contain only numbers."));
      if (value < 30 || value > 90)
        return Promise.reject(
          new Error("Tenure must be between 30 and 90 days.")
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const fundReqRules = (totalInv, maxFundRequiredPct = 0.8) => [
  {
    validator(_, value) {
      const resolvedPct = resolveMaxFundRequiredPct(maxFundRequiredPct);
      const maxAllowed = maxFundAllowedAmount(totalInv, resolvedPct);
      const pctLabel = parseFloat((resolvedPct * 100).toFixed(2));

      // Allow empty value, but validate 0
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const stringValue = String(value).trim();

      // Reject alphabets or special chars
      if (!/^\d+$/.test(stringValue)) {
        return Promise.reject("Only numbers are allowed");
      }

      const numericValue = Number(stringValue);

      if (numericValue < 80000) {
        return Promise.reject("Minimum funding amount is ₹80,000");
      }

      if (numericValue > maxAllowed) {
        return Promise.reject(
          `Cannot exceed ${pctLabel}% of Total Invoice Value (₹${maxAllowed})`
        );
      }
      if (!value) {
        return Promise.reject("Funding Amount is mandatory");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const panRules = [
  {
    required: true,
    message: `PAN cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value))
        return Promise.reject(new Error("Please enter a valid PAN"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const gstinRules = [
  {
    required: true,
    message: `GSTIN cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)
      )
        return Promise.reject(new Error("Please enter a valid GSTIN"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];


export const validateWebsiteUrl = (value) => {
  if (!value || !String(value).trim()) {
    return null;
  }

  const trimmed = String(value).trim();
  const host = trimmed
    .replace(/^(https?:\/\/)+/gi, "")
    .split(/[/?#]/)[0]
    .replace(/\.$/, "");

  // if (!host || !isValidWebsiteHost(host)) {
  //   return "Enter a valid website URL (e.g. example.com).";
  // }

  const normalized = `http://${host}`;
  try {
    new URL(normalized);
  } catch {
    return "Enter a valid website URL (e.g. example.com).";
  }

  if (trimmed.length > 250) {
    return "Website URL cannot exceed 250 characters.";
  }

  return null;
};

export const websiteRules = [
  {
    validator(_, value) {
      const error = validateWebsiteUrl(value);
      if (error) {
        return Promise.reject(error);
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const descriptionRules = (type) => [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject(`${type} Description cannot be empty!`);
      }

      if (value.length > 500) {
        return Promise.reject(
          `${type} Description cannot exceed 500 characters.`
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const financialSummaryRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Financial Summary cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject(
          "Financial Summary cannot exceed 500 characters."
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const CommentsRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Comments cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject("Comments cannot exceed 500 characters.");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const reasonRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Reason cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject("Reason cannot exceed 500 characters.");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const discountRateRules = [
  {
    required: true,
    message: `Interest Rate cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (value === null || value === undefined || value === "") {
        return Promise.resolve();
      }
      const str = String(value).trim();
      // Allow whole numbers or up to 2 decimal places (7, 7.5, 8.25)
      if (!/^\d+(\.\d{1,2})?$/.test(str)) {
        return Promise.reject(
          new Error(`Interest Rate must be a number (up to 2 decimals)!`),
        );
      }
      const num = Number(str);
      if (num < 0.1 || num > 50) {
        return Promise.reject(
          new Error(`Interest Rate must be between 0.1 and 50!`),
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const maxFundRequiredPctRules = [
  {
    required: true,
    message: "Max Fund Required % cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (value === null || value === undefined || value === "") {
        return Promise.resolve();
      }
      const str = String(value).trim();
      if (!/^\d+$/.test(str)) {
        return Promise.reject(
          new Error("Max Fund Required % must be a number!"),
        );
      }
      const num = Number(str);
      if (num < 60 || num > 80) {
        return Promise.reject(
          new Error("Max Fund Required % must be between 60 and 80!"),
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const businessSummaryRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Business Summary cannot be empty!");
      }

      if (value.length > 1000) {
        return Promise.reject(
          "Business Summary cannot exceed 1000 characters."
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const disputeAmtRules = (txnAmt) => [
  {
    required: true,
    message: `Dispute amount cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const disAmt = parseInt(value);
      if (!value) return Promise.resolve();
      if (disAmt > txnAmt)
        return Promise.reject(
          new Error(
            `Dispute amount cannot exceed transaction amount (${txnAmt}).`
          )
        );

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const UTR_ALLOWED_PATTERN = /^[A-Za-z0-9]+$/;
export const UTR_MIN_LENGTH = 12;
export const UTR_MAX_LENGTH = 30;

export const getUtrRules = ({ required = true, label = "UTR" } = {}) => [
  {
    validator: (_, value) => {
      const trimmed = (value ?? "").trim();

      if (!trimmed) {
        return required
          ? Promise.reject(new Error(`${label} is required`))
          : Promise.resolve();
      }

      if (/\s/.test(trimmed)) {
        return Promise.reject(new Error(`${label} cannot contain spaces`));
      }

      if (!UTR_ALLOWED_PATTERN.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} can contain only letters and numbers`)
        );
      }

      if (/^[A-Za-z]+$/.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} cannot contain only alphabets`)
        );
      }

      if (trimmed.length < UTR_MIN_LENGTH) {
        return Promise.reject(
          new Error(`${label} must be at least ${UTR_MIN_LENGTH} characters`)
        );
      }

      if (trimmed.length > UTR_MAX_LENGTH) {
        return Promise.reject(
          new Error(`${label} cannot exceed ${UTR_MAX_LENGTH} characters`)
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const REMARKS_MIN_LENGTH = 4;
export const REMARKS_MAX_LENGTH = 500;
export const REMARKS_ALLOWED_PATTERN = /^[A-Za-z0-9\s]+$/;

export const getRemarksRules = ({ required = false, label = "Remarks" } = {}) => [
  {
    validator: (_, value) => {
      const trimmed = (value ?? "").trim();

      if (!trimmed) {
        return required
          ? Promise.reject(new Error(`${label} is required`))
          : Promise.resolve();
      }

      if (trimmed.length < REMARKS_MIN_LENGTH) {
        return Promise.reject(
          new Error(`${label} must be at least ${REMARKS_MIN_LENGTH} characters`)
        );
      }

      if (trimmed.length > REMARKS_MAX_LENGTH) {
        return Promise.reject(
          new Error(`${label} cannot exceed ${REMARKS_MAX_LENGTH} characters`)
        );
      }

      if (/^[0-9\s]+$/.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} cannot contain only numbers`)
        );
      }

      if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} cannot contain only special characters`)
        );
      }

      if (!REMARKS_ALLOWED_PATTERN.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} can contain only letters, numbers, and spaces`)
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const APOB_MIN_LENGTH = 4;
export const APOB_MAX_LENGTH = 200;
export const APOB_ALLOWED_PATTERN = /^[A-Za-z0-9\s.,]+$/;

export const getApobRules = ({ required = true, label = "APOB" } = {}) => [
  {
    validator: (_, value) => {
      const trimmed = (value ?? "").trim();

      if (!trimmed) {
        return required
          ? Promise.reject(new Error(`${label} is required`))
          : Promise.resolve();
      }

      if (trimmed.length < APOB_MIN_LENGTH) {
        return Promise.reject(
          new Error(`${label} must be at least ${APOB_MIN_LENGTH} characters`)
        );
      }

      if (trimmed.length > APOB_MAX_LENGTH) {
        return Promise.reject(
          new Error(`${label} cannot exceed ${APOB_MAX_LENGTH} characters`)
        );
      }

      if (/^[0-9\s]+$/.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} cannot contain only numbers`)
        );
      }

      if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
        return Promise.reject(
          new Error(`${label} cannot contain only special characters`)
        );
      }

      if (!APOB_ALLOWED_PATTERN.test(trimmed)) {
        return Promise.reject(
          new Error(
            `${label} can contain only letters, numbers, spaces, commas, and periods`
          )
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const getDownloadSuccessMessage = (fileName) => `${fileName} downloaded successfully`;

export const downloadPageHandle = async ({ dispatch, type, screenName, dealType, dealCategoryId, statusId }) => {
  try {
    const formattedStatusId = Array.isArray(statusId) ? statusId.join(",") : statusId;
    const res = await dispatch(downloadPage({ type, screenName, dealType, dealCategoryId, statusId: formattedStatusId }));

    if (res.meta.requestStatus === "fulfilled") {
      if (!res.payload) {
        message.error("No data received from server");
        return;
      }

      const mimeType =
        type === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob =
        res.payload instanceof Blob
          ? res.payload
          : new Blob([res.payload], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      const fileName = type === "pdf" ? `${screenName}.pdf` : `${screenName}.xlsx`;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(getDownloadSuccessMessage(fileName));
    } else {
      message.error(res.payload?.message || "Unable to download file");
    }
  } catch (error) {
    message.error("An error occurred while downloading the file");
  }
};

export const detailedViewDownloadPageHandle = async ({ dispatch, type, id }) => {
  try {
    const res = await dispatch(detailedViewDownload({ type, id }));

    if (res.meta.requestStatus === "fulfilled") {
      if (!res.payload) {
        message.error("No data received from server");
        return;
      }

      const mimeType =
        type === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob =
        res.payload instanceof Blob
          ? res.payload
          : new Blob([res.payload], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      const fileName = `${type}_${id}.pdf`;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(getDownloadSuccessMessage(fileName));
    } else {
      message.error(res.payload?.message || "Failed to download file");
    }
  } catch (error) {
    message.error("An error occurred while downloading the file");
  }
};

export const validateFinancialTemplate = async (file, uploadKey) => {
  try {
    const config = FIN_TEMPLATE_CONFIG[uploadKey];

    if (!config) {
      return {
        valid: false,
        message: "Template configuration not found",
      };
    }

    const buffer = await file.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array",
    });

    const uploadedSheets = workbook.SheetNames;
    const expectedSheets = Object.keys(config.sheets);

    const unexpectedSheet = uploadedSheets.find(
      (sheetName) => !expectedSheets.includes(sheetName)
    );
    if (unexpectedSheet) {
      return {
        valid: false,
        message: `Unexpected sheet: ${unexpectedSheet}. Sheet names must match the template.`,
      };
    }

    for (const sheetName of expectedSheets) {
      if (!uploadedSheets.includes(sheetName)) {
        return {
          valid: false,
          message: `Missing sheet: ${sheetName}`,
        };
      }

      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      });

      if (!rows.length || !(rows[0] || []).length) {
        return {
          valid: false,
          message: `Sheet "${sheetName}" must include a header row`,
        };
      }

      if (rows.length < 2) {
        return {
          valid: false,
          message: `Sheet "${sheetName}" must include at least one data row`,
        };
      }

      const headers = (rows[0] || []).map((h) =>
        h == null ? "" : String(h).trim()
      );
      const metricHeaders = headers.slice(1);

      if (!metricHeaders.length) {
        return {
          valid: false,
          message: `Sheet "${sheetName}" must include at least one metric column`,
        };
      }

      if (metricHeaders.some((header) => !header)) {
        return {
          valid: false,
          message: `Empty metric header in sheet "${sheetName}"`,
        };
      }

      if (new Set(metricHeaders).size !== metricHeaders.length) {
        return {
          valid: false,
          message: `Duplicate metric headers in sheet "${sheetName}"`,
        };
      }

      const hasPeriodValue = rows.slice(1).some((row) => {
        const period = row?.[0];
        return period != null && String(period).trim() !== "";
      });

      if (!hasPeriodValue) {
        return {
          valid: false,
          message: `Sheet "${sheetName}" must include a period/year value in the first column`,
        };
      }
    }

    const hasAnyMetricValue = expectedSheets.some((sheetName) => {
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
      });
      return rows.slice(1).some((row) =>
        (row || []).slice(1).some(
          (cell) => cell != null && String(cell).trim() !== ""
        )
      );
    });

    if (!hasAnyMetricValue) {
      return {
        valid: false,
        message:
          "Please enter at least one metric value. Empty cells are not allowed for all metrics.",
      };
    }

    return {
      valid: true,
    };
  } catch (error) {
    return {
      valid: false,
      message: "Unable to read Excel file",
    };
  }
};

export const scTenureRules = [
  {
    required: true,
    message: `Tenure cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^\d+$/.test(value))
        return Promise.reject(new Error("Tenure should contain only numbers."));
      if (value < 30 || value > 120) {
        return Promise.reject(new Error("Tenure must be between 30 and 120 days."));
      }
      return Promise.resolve();
    },
  },
];

export {
  safeStringSort,
  safeNumberSort,
  safeDateSort,
  safeIdSort,
} from "./sortHelpers";